import { useEffect, useState, useMemo } from 'react';
import { geoMercator, geoPath, type GeoPermissibleObjects } from 'd3-geo';
import styles from './StateMiniMapSVG.module.css';
import {
	loadBrazilGeoJSON,
	getCachedBrazilGeoJSON,
	type BrazilGeoJSON,
} from '../../../utils/brazilGeoJSON';

const W = 60;
const H = 60;
const PADDING = 4;

interface StateMiniMapSVGProps {
	stateCode: string;
	isSelected: boolean;
	className?: string;
}

export const StateMiniMapSVG = ({ stateCode, isSelected, className }: StateMiniMapSVGProps) => {
	const [geoJson, setGeoJson] = useState<BrazilGeoJSON | null>(getCachedBrazilGeoJSON());

	useEffect(() => {
		const cached = getCachedBrazilGeoJSON();
		if (cached) {
			setGeoJson(cached);
			return;
		}
		loadBrazilGeoJSON()
			.then((data) => setGeoJson(data))
			.catch(() => {});
	}, []);

	const statePath = useMemo(() => {
		if (!geoJson) return null;
		const feature = geoJson.features.find((f) => f.properties?.sigla === stateCode);
		if (!feature) return null;
		const projection = geoMercator().fitExtent(
			[
				[PADDING, PADDING],
				[W - PADDING, H - PADDING],
			],
			feature as unknown as GeoPermissibleObjects
		);
		const pathGen = geoPath(projection);
		return pathGen(feature as unknown as GeoPermissibleObjects);
	}, [geoJson, stateCode]);

	if (!geoJson) {
		return <div className={`${styles.placeholder} ${className ?? ''}`} />;
	}

	if (!statePath) return null;

	return (
		<svg
			viewBox={`0 0 ${W} ${H}`}
			className={`${styles.svg} ${className ?? ''}`}
			preserveAspectRatio="xMidYMid meet"
		>
			<path
				d={statePath}
				className={`${styles.statePath} ${isSelected ? styles.stateSelected : styles.stateBase}`}
			/>
		</svg>
	);
};
